import {
  AlignedResultAll,
  AlignedSProblem,
  CurriculumLUnitResponse,
  DrillStartResponse,
  ResultProblemResponse,
} from '@/models/api/alignedCourse'
import { TabSelected } from '@/types/student/courses'
import { BookmarkDetailRes } from '@/views/student/v3/BookmarkAlignedDetail.vue'
import { Component, Vue } from 'vue-property-decorator'

@Component
export default class AlignedWebApi extends Vue {
  protected async drillStartAligned(params: {
    studyLogicType: string
    classModeCode?: string
    classCategoryCode?: string
    questionCode?: string
    targetCurriculum: {
      curriculumSUnitId: string | number
    }
  }): Promise<DrillStartResponse> {
    const result = await Vue.prototype.$http.httpWithToken.post('v3/aligned_course/drills/start', params)
    return result.data
  }

  protected async studyProblemAligned(params: {
    result_drill_id: string
  }): Promise<{
    problemSortNum: number
    classCategoryCode: string
    sProblemId: number
  }> {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/study_problems', { params })
    return result.data
  }

  protected async getProblemsStartAligned(
    alignedSProblemId: number | string,
    alignedCurriculumSUnitId: number | string,
    drillType?: string | null,
    questionCode?: string | null
  ): Promise<{
    resultDrillId: number
    problemSortNum: number
  }> {
    const payload = {
      aligned_curriculum_s_unit_id: alignedCurriculumSUnitId,
      drill_type: drillType,
      question_code: questionCode,
    }
    const result = await Vue.prototype.$http.httpWithToken.post(
      `v3/aligned_course/s_problems/${alignedSProblemId}/start`,
      payload
    )
    return result.data
  }

  protected async getProblemsAligned(
    alignedSProblemId: number | string,
    alignedCurriculumSUnitId: number | string,
    questionCode?: number | string
  ): Promise<AlignedSProblem> {
    const params = {
      aligned_curriculum_s_unit_id: alignedCurriculumSUnitId,
      question_code: questionCode,
    }
    const result = await Vue.prototype.$http.httpWithToken.get(`v3/aligned_course/s_problems/${alignedSProblemId}`, {
      params,
    })
    return result.data
  }

  protected async getMultipleProblemsAligned(sProblemsId: number[]): Promise<AlignedSProblem[]> {
    const params = {
      s_problem_ids: sProblemsId,
    }
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/s_problems', {
      params,
    })
    return result.data
  }

  protected async getResultProblemsAligned(params: {
    result_drill_id: string
    aligned_s_problem_id: string
    problem_sort_num: string
  }): Promise<ResultProblemResponse> {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/result_problems', { params })
    return result.data
  }

  protected async drillCompleteAligned(resultDrillId: string) {
    const params = {
      result_drill_id: resultDrillId,
    }

    Vue.prototype.$logger.info('-- WebApi drillCompleteAligned')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.post('v3/aligned_course/drills/complete', params)
    return result
  }

  // S UNIT
  protected async getAlignedCurriculumSUnits(curriculumLUnitId: number) {
    const params = {
      aligned_curriculum_l_unit_id: curriculumLUnitId,
    }
    Vue.prototype.$logger.info('-- WebApi getAlignedCurriculumSUnits')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/curriculum_s_units', {
      params,
    })
    return result.data
  }

  // L UNIT
  protected async getAlignedCurriculumLUnits(queryParams: {
    subjectId: number
    gradeId: number
    bookType: TabSelected
    page?: number
  }): Promise<CurriculumLUnitResponse> {
    const params = {
      subject_id: queryParams.subjectId,
      grade_id: queryParams.gradeId,
      book_type: queryParams.bookType,
      page: queryParams.page || 1,
    }

    Vue.prototype.$logger.info('-- WebApi getAlignedCurriculumLUnits')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/curriculum_l_units', { params })
    return result.data
  }

  protected async getAlignedImage(path: string): Promise<{ url: string }> {
    const params = {
      path,
    }

    Vue.prototype.$logger.info('-- WebApi getAlignedImage')
    Vue.prototype.$logger.info(params)

    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/image_url', { params })
    return result.data
  }

  protected async ocrScoreRegisterUnit(payload: {
    result_drill_id: string
    aligned_s_problem_id: string
    problem_sort_num: string
    ocr_data: any
  }): Promise<{ status: number }> {
    const formData = new FormData()

    formData.append('result_drill_id', payload.result_drill_id)
    formData.append('aligned_s_problem_id', payload.aligned_s_problem_id)
    formData.append('problem_sort_num', payload.problem_sort_num)

    payload.ocr_data.forEach((item: any, index: number) => {
      formData.append(`ocr_data[${index}][image]`, item.image)
      formData.append(`ocr_data[${index}][question_code]`, item.question_code)
      formData.append(`ocr_data[${index}][number]`, item.number)
      formData.append(`ocr_data[${index}][done]`, item.done as any)
      formData.append(`ocr_data[${index}][student_text_answer]`, item.student_text_answer as any)
      formData.append(`ocr_data[${index}][candidates]`, item.candidates as any)
    })

    const result = await Vue.prototype.$http.httpWithToken.post(
      'v3/aligned_course/result_problems/ocr_score_register_unit',
      formData
    )

    return result
  }

  protected async bookmarkQuestion(payload: { result_drill_id: number; question_code: string }) {
    const result = await Vue.prototype.$http.httpWithToken.post('v3/aligned_course/question_bookmarks', payload)

    return result?.data
  }

  protected async bookmarkQuestionDetail(questionBookmarkId: string): Promise<BookmarkDetailRes> {
    const result = await Vue.prototype.$http.httpWithToken.get(
      `v3/aligned_course/question_bookmarks/${questionBookmarkId}`
    )

    return result?.data
  }

  protected async getListBookmarkQuestions(params: { subject_code: number; page: string }) {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/question_bookmarks', { params })

    return result?.data
  }

  protected async unBookmarkQuestion(params: { result_drill_id: number; question_code: string }) {
    const result = await Vue.prototype.$http.httpWithToken.delete('v3/aligned_course/question_bookmarks', { params })

    return result?.data
  }

  protected async getBookmarkQuestions(params: {
    result_drill_id: number
    question_code: string
  }): Promise<{ isBookmarked: boolean }> {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/question_bookmarks/status', {
      params,
    })

    return result?.data
  }

  protected async saveMemoQuestion(payload: {
    memo_img: File
    result_drill_id: string
    question_code: string
    problem_sort_num: string
  }) {
    const formData = new FormData()

    formData.append('memo_img', payload.memo_img)
    formData.append('result_drill_id', payload.result_drill_id)
    formData.append('question_code', payload.question_code)
    formData.append('problem_sort_num', payload.problem_sort_num)

    const result = await Vue.prototype.$http.httpWithToken.post('v3/aligned_course/question_memos', formData)

    return result?.data
  }

  protected async viewMemoQuestion(params: {
    result_drill_id: string
    question_code: string
    problem_sort_num: string
  }) {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/question_memos/memo_image', {
      params,
    })

    return result?.data
  }

  protected async finishResultProblem(payload: {
    result_drill_id: number
    aligned_s_problem_id: number
    problem_sort_num: number
    corrects: boolean[]
  }): Promise<{ isBookmarked: boolean }> {
    const result = await Vue.prototype.$http.httpWithToken.post(
      'v3/aligned_course/result_problems/finish_register_unit',
      payload
    )

    return result?.data
  }

  protected async getResultDrillsAligned(resultDrillId: string): Promise<AlignedResultAll> {
    const result = await Vue.prototype.$http.httpWithToken.get(`v3/aligned_course/drills/${resultDrillId}`)

    return result.data
  }

  protected async getAlignedActiveSubject() {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/aligned_course/active_subjects')

    return result.data
  }

  protected async getCurriculumLUnit(params: { subject_id: number | string; grade_id: number | string }) {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/curriculum_l_units', { params })

    return result.data
  }

  protected async getCurriculumMUnit(params: { subject_id: number | string; grade_id: number | string }) {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/curriculum_m_units', { params })

    return result.data
  }

  protected async getCurriculumSUnit(params: {
    curriculum_m_unit_id: number | string
    study_logic_type: number | string
  }) {
    const result = await Vue.prototype.$http.httpWithToken.get('v3/curriculumSUnits', { params })

    return result.data
  }
}
